/**
 * Route Permission Utilities
 * Handles route-based permission checking and validation
 */

// Define route to permission mapping using patterns instead of exact names
export const ROUTE_PERMISSION_MAP = {
  '/users': {
    pattern: 'users',
    permissionNames: ['User Management', 'users', 'user management'],
    relatedRoutes: ['/:users/user-details/:id']
  },
  '/:users/user-details/:id': {
    pattern: 'user details',
    permissionNames: ['User Management', 'users', 'user management'],
    relatedRoutes: []
  },
  '/customers': {
    pattern: 'customers',
    permissionNames: ['Customer', 'customer', 'customers'],
    relatedRoutes: []
  },
  '/business-users': {
    pattern: 'business users',
    permissionNames: ['Business User', 'business user', 'business users'],
    relatedRoutes: []
  },
  '/product-category': {
    pattern: 'product category',
    permissionNames: ['Product Category', 'product category'],
    relatedRoutes: []
  },
  '/product-packaging': {
    pattern: 'product packaging',
    permissionNames: ['Product Packaging', 'product packaging'],
    relatedRoutes: []
  },
  '/coupons': {
    pattern: 'coupons',
    permissionNames: ['Coupon Usage History', 'coupon usage history', 'coupons'],
    relatedRoutes: []
  },
  '/referral-code': {
    pattern: 'referral code',
    permissionNames: ['Referral Code', 'referral code'],
    relatedRoutes: []
  },
  '/city-management': {
    pattern: 'city management',
    permissionNames: ['City Management', 'city management'],
    relatedRoutes: []
  },
  '/order-service': {
    pattern: 'order service',
    permissionNames: ['Order Service', 'order service'],
    relatedRoutes: []
  },
  '/order-status': {
    pattern: 'order status',
    permissionNames: ['Orders', 'orders', 'order status'],
    relatedRoutes: ['/order-status/details/:id']
  },
  '/order-status/details/:id': {
    pattern: 'order status details',
    permissionNames: ['Orders', 'orders', 'order status'],
    relatedRoutes: []
  },
  '/cashout-request': {
    pattern: 'cashout request',
    permissionNames: ['Cashout Request', 'cashout request'],
    relatedRoutes: []
  },
  '/inventory-package': {
    pattern: 'inventory package',
    permissionNames: ['Inventory Package', 'inventory package'],
    relatedRoutes: []
  },
  '/inventory-package-request': {
    pattern: 'inventory package request',
    permissionNames: ['Package Request', 'package request', 'inventory package request'],
    relatedRoutes: []
  },
  '/product-inventory-request': {
    pattern: 'product inventory request',
    permissionNames: ['Product Inventory Request', 'product inventory request'],
    relatedRoutes: ['/product-inventory-request/details/:id']
  },
  '/product-inventory-request/details/:id': {
    pattern: 'product inventory request details',
    permissionNames: ['Product Inventory Request', 'product inventory request'],
    relatedRoutes: []
  },
  '/zone': {
    pattern: 'zone',
    permissionNames: ['Zone', 'zone'],
    relatedRoutes: []
  },
  '/hq-address': {
    pattern: 'hq address',
    permissionNames: ['HQ Address', 'hq address'],
    relatedRoutes: []
  },
  '/package-dimension': {
    pattern: 'package dimension',
    permissionNames: ['Package Dimension', 'package dimension'],
    relatedRoutes: []
  },
  '/package-weight': {
    pattern: 'package weight',
    permissionNames: ['Package Weight', 'package weight'],
    relatedRoutes: []
  },
  '/package-opened-charges': {
    pattern: 'package opened charges',
    permissionNames: ['Package Opened Charges', 'package opened charges'],
    relatedRoutes: []
  },
  '/storage-units': {
    pattern: 'storage units',
    permissionNames: ['Storage Units', 'storage units', 'Storage Unit', 'storage unit'],
    relatedRoutes: []
  },

  '/log-issues': {
    pattern: 'log issues',
    permissionNames: ['Log Issue', 'log issue', 'log issues'],
    relatedRoutes: []
  },
  '/main-reasons': {
    pattern: 'main reasons',
    permissionNames: ['Log Issue Reasons', 'log issue reasons', 'main reasons'],
    relatedRoutes: ['/main-reasons/sub-reasons/:id']
  },
  '/main-reasons/sub-reasons/:id': {
    pattern: 'sub reasons',
    permissionNames: ['Log Issue Reasons', 'log issue reasons', 'sub reasons'],
    relatedRoutes: []
  },
  '/commission-amount': {
    pattern: 'commission amount',
    permissionNames: ['Commission Amount', 'commission amount'],
    relatedRoutes: []
  },
  '/billing-invoice-payment': {
    pattern: 'billing invoice payment',
    permissionNames: ['Billing Invoice Type', 'billing invoice type', 'billing invoice payment'],
    relatedRoutes: []
  },
  '/sub-admin': {
    pattern: 'sub admin',
    permissionNames: ['Sub Admin', 'sub admin'],
    relatedRoutes: []
  },
  '/roles': {
    pattern: 'roles',
    permissionNames: ['Roles', 'roles'],
    relatedRoutes: ['/roles/edit/:id', '/roles/add']
  },
  '/roles/add': {
    pattern: 'add role',
    permissionNames: ['Roles', 'roles'],
    relatedRoutes: []
  },
  '/roles/edit/:id': {
    pattern: 'edit role',
    permissionNames: ['Roles', 'roles'],
    relatedRoutes: []
  },
  '/permissions': {
    pattern: 'permissions',
    permissionNames: ['Permissions', 'permissions'],
    relatedRoutes: []
  },
  '/privacy-policy': {
    pattern: 'privacy policy',
    permissionNames: ['Privacy Policy', 'privacy policy'],
    relatedRoutes: []
  },
  '/terms-conditions': {
    pattern: 'terms conditions',
    permissionNames: ['Terms Conditions', 'terms conditions'],
    relatedRoutes: []
  },
  '/testimonials': {
    pattern: 'testimonials',
    permissionNames: ['Testimonials', 'testimonials'],
    relatedRoutes: []
  },
  '/contact-us': {
    pattern: 'contact us',
    permissionNames: ['Contact Us', 'contact us'],
    relatedRoutes: []
  },
  '/faqs': {
    pattern: 'faqs',
    permissionNames: ['FAQs', 'faqs'],
    relatedRoutes: []
  },
  '/blog': {
    pattern: 'blog',
    permissionNames: ['Blog', 'blog'],
    relatedRoutes: []
  },
  '/our-team': {
    pattern: 'our team',
    permissionNames: ['Our Team', 'our team'],
    relatedRoutes: []
  },
  '/billing-payment-management': {
    pattern: 'billing payment management',
    permissionNames: ['Billing Payment Management', 'billing payment management'],
    relatedRoutes: []
  },
  '/ratings': {
    pattern: 'ratings',
    permissionNames: ['Ratings', 'ratings'],
    relatedRoutes: []
  },
  '/notifications': {
    pattern: 'notifications',
    permissionNames: ['Notifications', 'notifications'],
    relatedRoutes: []
  },
  '/campaign-notifications': {
    pattern: 'campaign notifications',
    permissionNames: ['Send Push Alert', 'send push alert', 'campaign notifications'],
    relatedRoutes: []
  },
  '/sms-alert': {
    pattern: 'sms alert',
    permissionNames: ['Send SMS Alert', 'send sms alert', 'sms alert'],
    relatedRoutes: []
  },
  '/email-notification': {
    pattern: 'email notification',
    permissionNames: ['Send Email Alert', 'send email alert', 'email notification'],
    relatedRoutes: []
  }
}

export const PUBLIC_AUTHENTICATED_ROUTES = ['/401', '/404', '/', '/dashboard']

/**
 * Check if user has access to the given route based on permissions
 * @param {string} path - Current route path
 * @param {Array} permissionsWithNames - Array of permissions with names from getPermissionNames
 * @returns {boolean} - Whether user has access
 */
export const checkRouteAccess = (path, permissionsWithNames) => {
  // If no permissions provided, deny access
  if (!permissionsWithNames || permissionsWithNames.length === 0) {
    return false
  }

  // First check for exact route matches
  const basePath = Object.keys(ROUTE_PERMISSION_MAP).find(route => path === route || path.startsWith(route + '/'))

  if (basePath) {
    const routeConfig = ROUTE_PERMISSION_MAP[basePath]
    const requiredPermissionNames = routeConfig.permissionNames || []

    // Check if user has any of the required permissions for this route
    const hasRequiredPermission = permissionsWithNames.some(permission => {
      const permissionName = permission.permission_name || permission.name || ''
      const permNameLower = permissionName.toLowerCase()

      return requiredPermissionNames.some(requiredName => {
        const requiredNameLower = requiredName.toLowerCase()

        // Check for exact match or if permission name includes the required name
        const matches =
          permNameLower === requiredNameLower ||
          permNameLower.includes(requiredNameLower) ||
          requiredNameLower.includes(permNameLower)

        return matches && permission.read === true
      })
    })

    return hasRequiredPermission
  }

  // If no exact match found, check for dynamic matches
  const pathForMatching = path.split('/').filter(Boolean).join(' ').replace(/-/g, ' ').toLowerCase()

  // For dynamic routes, check if user has any read permissions
  const hasAnyReadPermission = permissionsWithNames.some(permission => permission.read === true)

  return hasAnyReadPermission
}

/**
 * Get accessible routes for a given set of permissions
 * @param {Array} permissionsWithNames - Array of permissions with names
 * @param {string} userType - Type of the user
 * @returns {array} - Array of accessible routes
 */
export const getAccessibleRoutes = (permissionsWithNames, userType) => {
  // If user is admin, return all routes
  if (userType === 'admin') {
    const allRoutes = [...PUBLIC_AUTHENTICATED_ROUTES, ...Object.keys(ROUTE_PERMISSION_MAP)]

    // Add all related routes
    Object.values(ROUTE_PERMISSION_MAP).forEach(config => {
      allRoutes.push(...config.relatedRoutes)
    })

    return allRoutes
  }

  if (!permissionsWithNames || permissionsWithNames.length === 0) {
    return PUBLIC_AUTHENTICATED_ROUTES
  }

  const accessibleRoutes = [...PUBLIC_AUTHENTICATED_ROUTES]

  // Add routes based on permission patterns
  Object.entries(ROUTE_PERMISSION_MAP).forEach(([route, config]) => {
    const requiredPermissionNames = config.permissionNames || []

    // Check if user has any of the required permissions for this route
    const hasRequiredPermission = permissionsWithNames.some(permission => {
      const permissionName = permission.permission_name || permission.name || ''
      return requiredPermissionNames.some(
        requiredName => permissionName.toLowerCase().includes(requiredName.toLowerCase()) && permission.read === true
      )
    })

    if (hasRequiredPermission) {
      accessibleRoutes.push(route)
      // Add related routes
      accessibleRoutes.push(...config.relatedRoutes)
    }
  })

  return accessibleRoutes
}

/**
 * Check if a route is public (doesn't require specific permissions)
 * @param {string} path - Route path to check
 * @returns {boolean} - Whether the route is public
 */
export const isPublicRoute = path => {
  return PUBLIC_AUTHENTICATED_ROUTES.some(route => path === route || path.startsWith(route + '/'))
}

/**
 * Get permission for a specific route
 * @param {string} path - Route path
 * @param {Array} permissionsWithNames - Array of permissions with names
 * @returns {Object|null} - Permission object or null if not found
 */
export const getRoutePermission = (path, permissionsWithNames) => {
  const basePath = Object.keys(ROUTE_PERMISSION_MAP).find(route => path === route || path.startsWith(route + '/'))

  if (basePath) {
    const routeConfig = ROUTE_PERMISSION_MAP[basePath]
    const requiredPermissionNames = routeConfig.permissionNames || []

    // Find permission that matches the route requirements and has read access
    return permissionsWithNames?.find(permission => {
      const permissionName = permission.permission_name || permission.name || ''
      return requiredPermissionNames.some(
        requiredName => permissionName.toLowerCase().includes(requiredName.toLowerCase()) && permission.read === true
      )
    })
  }

  return null
}

/**
 * Check if user has specific action permission for a route
 * @param {string} path - Route path
 * @param {Array} permissionsWithNames - Array of permissions with names
 * @param {string} action - Action to check (read, write, add, remove)
 * @returns {boolean} - Whether user has the specific action permission
 */
export const hasRouteActionPermission = (path, permissionsWithNames, action) => {
  const routePermission = getRoutePermission(path, permissionsWithNames)
  return routePermission ? routePermission[action] === true : false
}

/**
 * Get all permissions for a user
 * @param {Object} userPermissions - User permissions object
 * @returns {Array} - Array of permissions with permission_id
 */
export const getAllUserPermissions = userPermissions => {
  if (!userPermissions || typeof userPermissions !== 'object') return []

  return Object.entries(userPermissions).map(([permissionId, permissions]) => ({
    permission_id: parseInt(permissionId),
    ...permissions
  }))
}

/**
 * Check if user has any read permissions
 * @param {Object} userPermissions - User permissions object
 * @returns {boolean} - Whether user has any read permissions
 */
export const hasAnyReadPermissions = userPermissions => {
  if (!userPermissions || typeof userPermissions !== 'object') return false

  return Object.values(userPermissions).some(permission => permission.read === true)
}
